'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { useGetMarketSearchPopularQuery } from 'core/query/yapi/hk-web/MarketCalc/query'
import { Input, Popover, List, Flex, Spin, Tag } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useDebounceFn } from 'ahooks'
import { TPostMarketSearchResultResponse } from 'core/service/yapi/hk-web/MarketCalc/api'
import Tabs from '@/components/Tabs'
import { useColors, useGranularity } from '@/components/appStore'
import dayjs from 'dayjs'
import { useLocalStorageState } from 'ahooks'
import cls from 'classnames'
import NoData from '@/components/NoData'
import styles from './style.module.scss'
import { highlightText } from 'core/utils/searchHighlight'
import { useLocale } from 'core/components/Locale'
import { useHCRouter } from '@/components/HCNextLink'
import { useContractSocket } from 'core/websocket/contract'
import { showSpliceDecimal, spliceDecimal } from 'core/utils'
import { useTradeSocket } from 'core/websocket/trade'
import { Socket_Topic_Trade_Area_Tickers } from 'core/websocket/trade/createTradeMessageDataHandler'
import { useAuthenticate } from 'core/components/Authenticate'
import useWidth from 'core/hooks/useWidth'
import FavoriteCoinPair from 'core/components/FavoriteCoinPair'
import { getBaseContractCode, getDeliverContractCode } from 'core/utils/helper/contract/common'
import { useResponsiveDevice } from '@/hooks/useResponsiveDevice'
import { useGetMarketSearchResultQuery } from 'core/query/fixLegacyAPI/hk-web/MarketCalc/query'
// 合约类型 perpetual-永续 week-当周 nextWeek-次周 quarter-当季 nextQuarter-次季
type TContractType = 'week' | 'nextWeek' | 'quarter' | 'nextQuarter'
// 产品类型，2-现货 3-杠杆 4-永续合约 5-交割合约 6-期权
enum TInstType {
  SPOT = 2,
  MARGIN = 3,
  SWAP = 4,
  DELIVERY = 5,
  OPTION = 6,
}

const SearchComponent: React.FC = () => {
  const t = useLocale()
  const router = useHCRouter()
  const { contractGranularity: granularity } = useGranularity()
  const [riseColor, fallColor, r1Color, bgc22, c7] = useColors(['rise', 'fall', 'r1', 22, 7])
  const { width } = useWidth()
  const responsiveDevice = useResponsiveDevice()
  // Popover 的显示和隐藏
  const [visible, setVisible] = useState(false)
  // 搜索关键词
  const [keyword, setKeyword] = useState('')
  // 搜索结果激活的标签页
  const [activeTab, setActiveTab] = useState<string>('all')
  // 合约，现货更多展示
  const [swapMoreVisible, setSwapMoreVisible] = useState(false)
  const [spotMoreVisible, setSpotMoreVisible] = useState(false)
  const { loginData } = useAuthenticate()
  // 获取热门搜索
  const { data } = useGetMarketSearchPopularQuery(
    {},
    {
      enabled: visible,
      staleTime: 0,
    }
  )
  // 搜索接口 - 使用Query方式
  const { data: searchResultData, isLoading: isPending } = useGetMarketSearchResultQuery(
    { keyword: keyword.trim() },
    { enabled: !!keyword.trim() }
  )

  const searchResult = keyword.trim() ? searchResultData?.data?.data || {} : {}

  const [recentSearchList, setRecentSearchList] = useLocalStorageState<any[]>('header_search_history', {
    defaultValue: [],
  })

  const contractSocketData = useContractSocket({
    biz: 'perpetual',
    type: 'tickers',
    zip: false,
    serialize: true,
    granularity,
  })

  const tradeAreaTickersData = useTradeSocket(
    {
      topicKey: Socket_Topic_Trade_Area_Tickers,
    },
    (data) => {
      if (!data) return
      return data?.map((item) => ({
        ...item,
        tradeId: item.tradeid,
        isOpen: item.isopen,
      }))
    }
  )

  // console.log('contractSocketData:', contractSocketData)
  // console.log('tradeAreaTickersData:', tradeAreaTickersData)

  useEffect(() => {
    if (!visible) {
      setKeyword('')
      setActiveTab('all')
      setSwapMoreVisible(false)
      setSpotMoreVisible(false)
    }
  }, [visible])

  const handlePopoverVisibleChange = (visible: boolean) => {
    if (responsiveDevice === 'pc') {
      setVisible(visible)
    }
    if (!visible) {
      setKeyword('')
      setActiveTab('all')
      setSwapMoreVisible(false)
      setSpotMoreVisible(false)
    }
  }

  // 当响应式为pad phone时关闭Popover  防止点开后缩小窗口还是打开的
  useEffect(() => {
    if (responsiveDevice !== 'pc') {
      setVisible(false)
    }
  }, [responsiveDevice])

  const handleSearch = (value: string) => {
    setKeyword(value)
  }

  const { run: debouncedHandleSearch } = useDebounceFn(handleSearch, {
    wait: 300, // 设置防抖时间，例如 300 毫秒
  })

  const popularSearches = useMemo(() => {
    // contractSocketData匹配data?.data.data
    const popularSearchesData = data?.data.data
    return (
      popularSearchesData?.map((item, index) => {
        const baseResult = {
          no: index + 1,
          ...item,
        }

        if (!item.contractCode) {
          const matchedSpot = tradeAreaTickersData?.find((spot) => spot.tradeId === item.tradeId)
          return matchedSpot
            ? {
                ...baseResult,
                last: spliceDecimal(matchedSpot.last!, item.digit ? item.digit.split('#') : [2]),
                change: spliceDecimal(matchedSpot.change!, 2),
              }
            : baseResult
        }

        const matchedContract = contractSocketData?.find(
          (contract) => contract.contractCode?.toUpperCase() === item.contractCode
        )

        return matchedContract
          ? {
              ...baseResult,
              last: matchedContract.last,
              change: showSpliceDecimal(matchedContract.changepercentage, 2),
            }
          : baseResult
      }) || []
    )
  }, [contractSocketData, data?.data.data, tradeAreaTickersData])

  // 最多展示4个
  const MAX_ITEMS = 4

  const swapMoreCount = useMemo(() => {
    const swapCount = searchResult?.swaps?.length || 0
    return swapCount > MAX_ITEMS ? swapCount - MAX_ITEMS : 0
  }, [searchResult])

  const spotMoreCount = useMemo(() => {
    const spotCount = searchResult?.spots?.length || 0
    return spotCount > MAX_ITEMS ? spotCount - MAX_ITEMS : 0
  }, [searchResult])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const storeRecentSearchList = (value) => {
    // 储存最近5条搜索，如果有traceId，判断traceId，如果没有，判断contractCode
    const copyRecentSearchList = recentSearchList ? [...recentSearchList] : []
    const index = copyRecentSearchList.findIndex((item) => {
      if (item.traceId) {
        return item.traceId === value.traceId
      }
      return item.contractCode === value.contractCode
    })
    if (index !== -1) {
      copyRecentSearchList.splice(index, 1)
    }
    copyRecentSearchList.unshift(value)
    if (copyRecentSearchList.length > 5) {
      copyRecentSearchList.pop()
    }
    setRecentSearchList(copyRecentSearchList)
  }

  const clearSearchHistory = () => {
    setRecentSearchList([])
  }

  const handleListItemClick = (item: any, shouldSave?: boolean) => {
    if ((searchResult?.result && keyword) || shouldSave) {
      storeRecentSearchList(item)
    }
    // 跳转网页
    if (item.tradeId) {
      let _tradeName = item.baseCurrency.toLowerCase() + '_' + item.quoteCurrency.toLowerCase()
      //etf交易区手动设置
      if (item.tradeArea == 6) {
        _tradeName = item.baseCurrency.toLowerCase() + '_' + 'etf'
      }
      //alts交易区
      if (item.tradeArea == 8) {
        _tradeName = item.baseCurrency.toLowerCase() + '_' + 'alts'
      }
      router.push(`/trade/exchange/?tradeCode=${_tradeName}`)
      return
    }
    if (item.contractType === 'perpetual') {
      // 永续
      router.push(`/contract/exchange/${getBaseContractCode(item.contractCode)}`)
      return
    } else if (item.contractType) {
      // 交割
      router.push(`/contract/exchange/${getDeliverContractCode(item.contractCode, item.contractType)}`)
      return
    }
  }

  const renderContractSubTitle = (instType: TInstType, contract: TContractType, liquidationDate: number) => {
    if (instType === TInstType.SWAP) {
      return t('contractExchange_016')
    }
    if (instType === TInstType.DELIVERY) {
      // 1735286400000转化为MMdd变成1227
      const date = dayjs(liquidationDate).format('MMDD')
      switch (contract) {
        case 'week':
          return `${t('contract_375')} ${date}`
        case 'nextWeek':
          return `${t('contract_376')} ${date}`
        case 'quarter':
          return `${t('contract_377')} ${date}`
        case 'nextQuarter':
          return `${t('contract_378')} ${date}`
        default:
          return ``
      }
    }
  }

  const renderStar = (tradeId: number, contractCode?: string) => {
    return (
      <FavoriteCoinPair type={contractCode ? 'contract' : 'spot'} tradeId={tradeId} contractCode={contractCode}>
        {({ isFavorite, toggleFavorite }) => (
          <i className={`iconfont iconbianzu30 cursor-pointer ${isFavorite ? 'c12' : 'c9'}`} onClick={toggleFavorite} />
        )}
      </FavoriteCoinPair>
    )
  }

  const renderList = (header: string, dataSource?: any[]) => {
    if (!dataSource || dataSource.length === 0) {
      return null
    }
    return (
      <List
        split={false}
        header={<div>{header}</div>}
        dataSource={dataSource}
        renderItem={(item) => {
          const {
            no,
            baseCurrency,
            quoteCurrency,
            webLogo,
            last,
            change: originChange,
            isMarginTrade,
            marginRatio,
            instType,
            contractType,
            liquidationDate,
            tradeId,
            contractCode,
          } = item
          const spliceDecimalChange = showSpliceDecimal(originChange, 2)
          const change = spliceDecimalChange >= 0 ? `+${spliceDecimalChange}` : spliceDecimalChange
          return (
            <List.Item onClick={() => handleListItemClick(item)} className="cursor-pointer">
              <Flex gap={10} align="center">
                {no && <div style={no < 4 ? { color: r1Color } : {}}>{no}</div>}
                <div>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={webLogo} alt="" style={{ width: 26, height: 26 }} />
                </div>
                <div>
                  <Flex gap={4}>
                    <Flex vertical>
                      {no ? (
                        <div>
                          {baseCurrency}
                          <span className="c7">{`/${quoteCurrency}`}</span>
                        </div>
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: highlightText(keyword, `${baseCurrency}/${quoteCurrency}`),
                          }}
                        ></div>
                      )}
                      <div className="c8">{renderContractSubTitle(instType, contractType, liquidationDate)}</div>
                    </Flex>
                    {isMarginTrade && (
                      <div className="c12 bgco3" style={{ padding: '0 3px' }}>
                        {marginRatio}X
                      </div>
                    )}
                    {no <= 3 && <div className="hot-img"></div>}
                  </Flex>
                </div>
              </Flex>
              <Flex gap={15} align="center">
                <div className="text-right">
                  <div>{last}</div>
                  <div style={{ color: change >= 0 ? riseColor : fallColor }}>{change}%</div>
                </div>
                <div>
                  {/* <StarOutlined className="cursor-pointer" onClick={() => handleCollect({ tradeId, contractCode })} /> */}
                  {renderStar(tradeId, contractCode)}
                </div>
              </Flex>
            </List.Item>
          )
        }}
      />
    )
  }

  const searchTabsItems = [
    {
      key: 'all',
      label: t('contractOrder_035'),
    },
    {
      key: 'swap',
      label: t('contractList_005'),
    },
    {
      key: 'spot',
      label: t('coinTrade_order_016'),
    },
  ]

  const swapsData = useMemo(() => {
    // contractSocketData匹配searchResult.swaps
    const matchedContracts = contractSocketData?.filter((contract) => {
      return searchResult.swaps?.find((item) => item.contractCode === contract.contractCode.toUpperCase())
    })
    const swaps = searchResult.swaps?.map((contract) => {
      const matchedItem = matchedContracts?.find((item) => contract.contractCode === item.contractCode.toUpperCase())
      return matchedItem
        ? {
            ...contract,
            last: matchedItem.last,
            change: showSpliceDecimal(matchedItem.changepercentage, 2),
          }
        : contract
    })
    return swapMoreVisible ? swaps : swaps?.slice(0, MAX_ITEMS)
  }, [contractSocketData, searchResult.swaps, swapMoreVisible])

  const spotsData = useMemo(() => {
    // tradeAreaTickersData匹配searchResult.spots
    const matchedSpots = tradeAreaTickersData?.filter((spot) => {
      return searchResult.spots?.find((item) => item.tradeId === spot.tradeId)
    })
    const spots = searchResult.spots?.map((spot) => {
      const matchedItem = matchedSpots?.find((item) => item.tradeId === spot.tradeId)
      return matchedItem
        ? {
            ...spot,
            last: spliceDecimal(matchedItem.last!, spot.digit ? spot.digit.split('#') : [2]),
            change: spliceDecimal(matchedItem.change!, 2),
          }
        : spot
    })
    return spotMoreVisible ? spots : spots?.slice(0, MAX_ITEMS)
  }, [tradeAreaTickersData, searchResult.spots, spotMoreVisible])

  const renderSearchTabContent = () => {
    if (activeTab === 'all') {
      return (
        <>
          {renderList(t('contractList_005'), swapsData)}
          {swapMoreCount > 0 && (
            <Flex
              className="cursor-pointer text-xs link"
              onClick={() => {
                setSwapMoreVisible(!swapMoreVisible)
              }}
              align="center"
              gap={4}
            >
              {swapMoreVisible ? `${t('market_079')}` : `${t('financial_009')}(${swapMoreCount})`}
              <i
                className={cls([
                  'iconfont',
                  'iconbianzu22',
                  'font-normal',
                  {
                    'rotate-180': swapMoreVisible,
                  },
                ])}
              ></i>
            </Flex>
          )}
          {renderList(t('coinTrade_order_016'), spotMoreVisible ? spotsData : spotsData?.slice(0, MAX_ITEMS))}
          {spotMoreCount > 0 && (
            <Flex
              className="cursor-pointer text-xs link"
              onClick={() => {
                setSpotMoreVisible(!spotMoreVisible)
              }}
              align="center"
              gap={4}
            >
              {spotMoreVisible ? `${t('market_079')}` : `${t('financial_009')}(${spotMoreCount})`}
              <i
                className={cls([
                  'iconfont',
                  'iconbianzu22',
                  'font-normal',
                  {
                    'rotate-180': spotMoreVisible,
                  },
                ])}
              ></i>
            </Flex>
          )}
        </>
      )
    }
    if (activeTab === 'swap') {
      return <>{renderList(t('contractList_005'), swapsData)}</>
    }
    if (activeTab === 'spot') {
      return <>{renderList(t('coinTrade_order_016'), spotsData)}</>
    }
  }

  const renderNoResultRecommend = () => {
    return (
      <Flex vertical gap={14}>
        <div>{t('market_080')}</div>
        <Flex wrap gap={16} justify="center">
          {popularSearches
            .slice(0, MAX_ITEMS)
            .map(
              ({
                tradeId,
                webLogo,
                baseCurrency,
                quoteCurrency,
                last,
                change,
                instType,
                contractType,
                liquidationDate,
              }) => (
                <Flex vertical gap={18} key={tradeId} className={cls([styles['recommend-item'], 'bgc17'])}>
                  <Flex gap={6} align="center">
                    <div>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={webLogo} alt="" style={{ width: 22, height: 22 }} />
                    </div>
                    <div>
                      <Flex gap={4}>
                        <div>
                          {baseCurrency}
                          <span>{`/${quoteCurrency}`}</span>
                        </div>
                        <div className="c12 bgco3 f12 whitespace-nowrap" style={{ padding: '0 3px' }}>
                          {renderContractSubTitle(instType, contractType, liquidationDate)}
                        </div>
                      </Flex>
                    </div>
                  </Flex>
                  <div>
                    <div>{last}</div>
                    <div style={{ color: change >= 0 ? riseColor : fallColor }}>{change}%</div>
                  </div>
                </Flex>
              )
            )}
        </Flex>
      </Flex>
    )
  }

  const renderRecentSearch = () => {
    if (!recentSearchList || recentSearchList.length === 0) {
      return null
    }
    return (
      <Flex vertical gap={14} className="mt-4">
        <Flex justify="space-between">
          <div className="f16">{t('market_078')}</div>
          <div className="link" onClick={clearSearchHistory}>
            {t('accountCoinTrade_289')}
          </div>
        </Flex>
        <Flex gap={14} wrap>
          {recentSearchList?.map((item) => {
            const {
              traceId,
              contractCode,
              baseCurrency,
              quoteCurrency,
              instType,
              contractType,
              liquidationDate,
              isMarginTrade,
              marginRatio,
            } = item
            return (
              <Tag
                className="cursor-pointer f14 font-normal"
                key={traceId || contractCode}
                style={{ padding: '4px 6px', backgroundColor: bgc22, border: 'none', color: c7 }}
                onClick={() => handleListItemClick(item, true)}
              >
                <Flex gap={10} align="center">
                  <div>
                    {baseCurrency}
                    {`/${quoteCurrency}`}
                  </div>
                  {isMarginTrade ? (
                    <div className="c12 bgco3" style={{ padding: '0 3px' }}>
                      {marginRatio}X
                    </div>
                  ) : (
                    <div className="c8">{renderContractSubTitle(instType, contractType, liquidationDate)}</div>
                  )}
                </Flex>
              </Tag>
            )
          })}
        </Flex>
      </Flex>
    )
  }

  const renderContent = () => {
    if (isPending) {
      return (
        <Flex justify="center">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </Flex>
      )
    }
    if (keyword) {
      if (searchResult?.result) {
        return (
          <div>
            <Tabs onChange={handleTabChange} activeKey={activeTab} items={searchTabsItems} />
            {renderSearchTabContent()}
          </div>
        )
      }
      return (
        <div className={styles['noResult']}>
          <NoData text="market_075" text_sub="market_076" />
          {renderNoResultRecommend()}
        </div>
      )
    }
    return (
      <div>
        {renderRecentSearch()}
        {renderList(t('market_080'), popularSearches)}
      </div>
    )
  }

  const content = (
    <div style={{ padding: '11px 8px' }} className="font-semibold">
      {width < 1600 && (
        <Input
          className="font-normal"
          placeholder={t('market_077')}
          size="large"
          onChange={(e) => debouncedHandleSearch(e.target.value)}
          style={{ borderRadius: '20px', fontSize: '14px', height: '40px' }}
          prefix={<i className="iconfont iconsousuo-24 f20 c9"></i>}
        />
      )}
      {renderContent()}
    </div>
  )

  return (
    <Popover
      arrow={false}
      content={content}
      trigger="click"
      open={visible}
      onOpenChange={handlePopoverVisibleChange}
      align={{
        offset: [0, 10],
      }}
      styles={{ body: { width: 400, height: 662, overflowY: 'auto' } }}
      destroyTooltipOnHide
      classNames={{ root: cls(styles.popoverContent) }}
      autoAdjustOverflow={true}
      placement="bottom"
    >
      {width < 1600 ? (
        <div className={styles.searchIcon}>
          <i className="iconfont iconsousuo-24 f20"></i>
        </div>
      ) : (
        <Input
          className="font-normal"
          placeholder={t('market_077')}
          size="large"
          onChange={(e) => debouncedHandleSearch(e.target.value)}
          style={{ borderRadius: '20px', fontSize: '14px', height: '32px', width: '200px' }}
          prefix={<i className="iconfont iconsousuo-24 f20 c9"></i>}
        />
      )}
      {/*<SearchOutlined className={styles.searchIcon} />*/}
      {/*<Button type="text" icon={<SearchOutlined />} />*/}
    </Popover>
  )
}

export default SearchComponent
